import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { ToastService } from '../../../../../shared/services/toast.service';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketPriority } from '../../../models/tickets-config.model';

@Component({
  selector: 'app-tickets-priorities-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './priorities-form.component.html'
})
export class TicketsPrioritiesFormComponent implements OnDestroy {
  @Input() drawerMode = true;
  @Output() saved = new EventEmitter<TicketPriority>();
  @Output() cancelled = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private configApi = inject(TicketsConfigApiService);
  private toast = inject(ToastService);

  isEditing = false;
  itemId: number | null = null;
  saving = false;
  error: string | null = null;
  private originalName = '';

  form: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(50)]],
    label: ['', [Validators.required, Validators.maxLength(50)]],
    color: ['#94a3b8', [Validators.maxLength(30)]],
    level: [3, [Validators.required, Validators.min(1), Validators.max(99)]],
    isActive: [true]
  });

  private readonly destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reset(): void {
    this.isEditing = false;
    this.itemId = null;
    this.error = null;
    this.saving = false;
    this.form.reset({
      name: '',
      label: '',
      color: '#94a3b8',
      level: 3,
      isActive: true
    });
  }

  loadPriority(id: number): void {
    this.isEditing = true;
    this.itemId = id;
    this.configApi.getPriorityById(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.form.patchValue({
          name: item.name,
          label: item.label,
          color: item.color || '#94a3b8',
          level: item.level,
          isActive: item.isActive
        });
        this.originalName = item.label;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la prioridad';
      }
    });
  }

  hasError(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = null;
    const raw = this.form.value;
    const req = {
      name: raw.name,
      label: raw.label,
      color: raw.color || undefined,
      level: Number(raw.level),
      isActive: raw.isActive
    };

    const obs = this.isEditing && this.itemId != null
      ? this.configApi.updatePriority(this.itemId, req)
      : this.configApi.createPriority(req);

    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.saving = false;
        if (this.isEditing) this.toast.updated('Prioridad',
          this.originalName !== (raw.label?.trim() || '')
            ? `Se actualizó "${this.originalName}" → "${raw.label?.trim() || ''}".`
            : `Se actualizó "${raw.label?.trim() || ''}" correctamente.`);
        else this.toast.created('Prioridad', `Se creó "${raw.label?.trim() || ''}" correctamente.`);
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'Error al guardar la prioridad';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  cancelar(): void {
    this.cancelled.emit();
  }
}
